import { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import toast from "react-hot-toast";

import { AuthContext } from "../../../../../../shared/context/auth-context";
import { useHttpClient } from "../../../../../../shared/hooks/http-hook";
import "./ModalUrgencia.scss";

/**
 * O que a secretária preenche quando alguém chega com dor.
 *
 * ─── PORQUE É UM MODAL E NÃO UM BOTÃO SÓ ────────────────────────────────────
 *
 * Era um botão: um clique e a urgência estava marcada, sem dizer ao que a
 * pessoa vinha nem a quem avisar. Quem recebia via um número subir e tinha de
 * descer à sala de espera para saber o que era.
 *
 * ─── O MOTIVO É UM TOQUE, NÃO UMA FRASE ─────────────────────────────────────
 *
 * Quem preenche isto tem uma pessoa com dor à frente. Se fosse só texto livre,
 * vinha vazio ou vinha «dor». Um toque mais cinco palavras é o que se escreve
 * de verdade — e o toque é também o que permite contar depois.
 *
 * ─── OS MÉDICOS COMEÇAM TODOS ESCOLHIDOS ────────────────────────────────────
 *
 * Escolher tem de ser um estreitamento, nunca a porta de entrada. Se fosse
 * preciso escolher, num dia cheio escolhia-se o primeiro nome ou saltava-se o
 * passo — e a pessoa com dor esperava. O dentista que já viu este paciente vem
 * destacado, porque é quem conhece o caso.
 */
function ModalUrgencia({ aberto, fechar, clienteId, clinicaId, aoMarcar }) {
  const auth = useContext(AuthContext);
  const { sendRequest } = useHttpClient();

  const [aCarregar, setACarregar] = useState(true);
  const [aEnviar, setAEnviar] = useState(false);
  const [motivos, setMotivos] = useState([]);
  const [medicos, setMedicos] = useState([]);
  const [sugerido, setSugerido] = useState(null);

  const [motivo, setMotivo] = useState("");
  const [descricao, setDescricao] = useState("");
  /* `null` = todos. Passa a ser uma lista só quando alguém desmarca alguém. */
  const [escolhidos, setEscolhidos] = useState(null);

  useEffect(() => {
    if (!aberto) return;

    let vivo = true;
    const carregar = async () => {
      setACarregar(true);
      try {
        const res = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/urgencias/contexto/${clienteId}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        );
        if (!vivo) return;
        setMotivos(res.motivos || []);
        setMedicos(res.medicos || []);
        setSugerido(res.sugerido || null);
      } catch (err) {
        if (vivo) toast.error("Não foi possível carregar os dados da urgência.");
      } finally {
        if (vivo) setACarregar(false);
      }
    };
    carregar();

    /* Sai-se do modal antes de a resposta chegar e o estado era escrito num
       componente já fechado. */
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, clienteId, auth.token]);

  /* Reabrir o modal não deve trazer o que se escreveu da vez anterior. */
  useEffect(() => {
    if (aberto) {
      setMotivo("");
      setDescricao("");
      setEscolhidos(null);
    }
  }, [aberto]);

  const todosEscolhidos = escolhidos === null;
  const estaEscolhido = (id) =>
    todosEscolhidos || escolhidos.some((e) => String(e) === String(id));

  const alternarMedico = (id) => {
    setEscolhidos((atual) => {
      const base = atual === null ? medicos.map((m) => String(m._id)) : atual;
      const sId = String(id);
      const novo = base.includes(sId)
        ? base.filter((x) => x !== sId)
        : [...base, sId];
      /* Voltar a ter todos é o mesmo que «todos» — evita gravar uma lista
         igual à unidade inteira, que depois congelava quem foi avisado se
         alguém entrasse na equipa. */
      return novo.length === medicos.length ? null : novo;
    });
  };

  /* ─── DESMARCAR TODOS NÃO É «NINGUÉM» ──────────────────────────────────────
   *
   * No servidor, uma lista vazia significa TODOS os dentistas da unidade — é
   * assim para que o caso normal não obrigue a escolher ninguém. Consequência:
   * desmarcar todos no ecrã avisava todos, exactamente o contrário do que o
   * gesto sugere.
   *
   * O servidor não consegue distinguir «todos» de «ninguém»: são o mesmo `[]`.
   * Por isso a guarda está aqui, e é a de não deixar gravar. Uma urgência que
   * não avisa ninguém também não serve de nada. */
  const nenhumEscolhido = Array.isArray(escolhidos) && escolhidos.length === 0;

  const marcar = async () => {
    if (nenhumEscolhido) {
      toast.error("Escolha pelo menos um dentista para avisar.");
      return;
    }
    setAEnviar(true);
    try {
      const corpo = {
        atendido: false,
        clienteId,
        clinicaId,
        motivo: motivo || undefined,
        descricao: descricao.trim() || undefined,
        /* Vazio = todos os dentistas da unidade, decidido no servidor. */
        avisarMedicos: todosEscolhidos ? [] : escolhidos,
      };

      const res = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/urgencias`,
        "POST",
        JSON.stringify(corpo),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );

      if (res.urgenciaMarcada) {
        toast("Este paciente já tinha uma urgência aberta.");
      } else {
        toast.success("Urgência marcada. A equipa foi avisada.");
      }
      aoMarcar?.();
      fechar();
    } catch (err) {
      toast.error(err.message || "Não foi possível marcar a urgência.");
    } finally {
      setAEnviar(false);
    }
  };

  const nrAvisados = todosEscolhidos ? medicos.length : escolhidos.length;
  const podeMarcar = !aCarregar && !aEnviar && !nenhumEscolhido;

  return (
    <Dialog open={aberto} onClose={fechar} maxWidth="sm" fullWidth>
      <DialogTitle className="modalUrgencia__titulo">
        Marcar urgência
      </DialogTitle>

      <DialogContent dividers>
        {aCarregar ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Ao que vem
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {motivos.map((m) => (
                  <Chip
                    key={m}
                    label={m}
                    onClick={() => setMotivo(motivo === m ? "" : m)}
                    color={motivo === m ? "error" : "default"}
                    variant={motivo === m ? "filled" : "outlined"}
                  />
                ))}
              </Box>
            </Box>

            <TextField
              label="Detalhes"
              placeholder="Partiu o dente ao cair. Dói ao morder."
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              multiline
              rows={3}
              inputProps={{ maxLength: 300 }}
              helperText={`${descricao.length}/300`}
              fullWidth
            />

            <Box>
              <Typography variant="subtitle2">Avisar</Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: "block", mb: 1 }}
              >
                {todosEscolhidos
                  ? `Todos os dentistas desta unidade (${medicos.length}).`
                  : `${nrAvisados} de ${medicos.length}.`}{" "}
                Quem não for avisado continua a ver a fila.
              </Typography>

              {medicos.length === 0 && (
                <Typography variant="body2" color="error">
                  Não há nenhum dentista ativo nesta unidade. A urgência fica
                  registada, mas ninguém será avisado.
                </Typography>
              )}

              {nenhumEscolhido && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                  Escolha pelo menos um dentista. Uma urgência que não avisa
                  ninguém não serve de nada.
                </Typography>
              )}

              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {medicos.map((m) => (
                  <FormControlLabel
                    key={m._id}
                    control={
                      <Checkbox
                        size="small"
                        checked={estaEscolhido(m._id)}
                        onChange={() => alternarMedico(m._id)}
                      />
                    }
                    label={
                      <span>
                        {m.name}
                        {String(m._id) === String(sugerido) && (
                          <Chip
                            size="small"
                            label="já viu este paciente"
                            sx={{ ml: 1 }}
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </span>
                    }
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={fechar} disabled={aEnviar}>
          Cancelar
        </Button>
        <Button
          onClick={marcar}
          variant="contained"
          color="error"
          disabled={!podeMarcar}
        >
          {aEnviar ? "A marcar…" : "Marcar urgência"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ModalUrgencia;
